import type { CSSProperties } from 'react';
import { Text } from './Text';
import { cx } from '@/lib/cx';
import { colors } from '@/theme/tokens';
import styles from './Button.module.css';

export type ButtonVariant =
  /** Navy gradient — the single dominant action on a screen (＋ ຂາຍ, ຈ່າຍເລີຍ). */
  | 'primary'
  /** White with a navy outline — the paired secondary action (ທວງໜີ້). */
  | 'outline'
  /** Sky fill on navy text — money-in actions (ຮັບຊຳລະ, ຕິດໜີ້ໄວ້). */
  | 'accent'
  /** Flat navy — chrome-level actions inside dark surfaces. */
  | 'solid';

interface ButtonProps {
  label: string;
  /** Small caption under the label, e.g. "ເງິນສົດ / QR". */
  sub?: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** Set on a genuine form submit; otherwise a plain button. */
  type?: 'button' | 'submit';
}

export function Button({
  label,
  sub,
  onPress,
  variant = 'primary',
  disabled = false,
  size = 15,
  className,
  style,
  type = 'button',
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onPress}
      className={cx(styles.base, styles[variant], className)}
      style={style}
    >
      <Text weight={800} size={size} color={textColor(variant)} align="center">
        {label}
      </Text>
      {sub ? (
        <Text size={10} color={subColor(variant)} align="center" className={styles.sub}>
          {sub}
        </Text>
      ) : null}
    </button>
  );
}

function textColor(v: ButtonVariant): string {
  if (v === 'outline') return colors.navy;
  if (v === 'accent') return colors.navy;
  return colors.surfaceAlt;
}

function subColor(v: ButtonVariant): string {
  if (v === 'accent') return colors.navy;
  if (v === 'outline') return colors.textMuted;
  return colors.textFaint;
}
